import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, chapterData, categories } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block"></i>
            <p className="text-foreground-600 mb-4">漫画未找到</p>
            <Link to="/category/all" className="text-primary-500 hover:text-primary-600 font-medium">
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const chapters = chapterData[comic.id] || [];
  const related = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫在线观看_免费下拉式漫画_韩漫之家`}
        description={`韩漫之家免费提供${comic.title}在线观看，${comic.description.slice(0, 100)}。下拉式阅读，智能加载，流畅舒适的韩漫阅读体验。`}
        keywords={`${comic.title},韩漫在线观看,免费漫画,下拉式,韩漫之家,${comic.tags.join(',')}`}
        canonical={`https://hanmanzhijia.com/comic/${comic.id}`}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: `${comic.title} - 韩漫之家`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: '2026-01-01',
          dateModified: comic.updateTime,
          publisher: {
            '@type': 'Organization',
            name: '韩漫之家',
          },
        }}
      />

      <Navbar />
      <main className="pt-20 pb-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName, path: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-72 flex-shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden sticky top-24">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫在线观看`}
                  title={`${comic.title} - 韩漫之家免费下拉式漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-600 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full">
                  {categoryName}
                </span>
                {comic.status === 'ongoing' && (
                  <span className="px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full">
                    连载中
                  </span>
                )}
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-star-fill text-accent-500"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-eye-line mr-1"></i>
                  {formatViews(comic.views)}
                </span>
                <span className="text-sm text-foreground-600">
                  更新至 {comic.chapters} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-background-100 text-foreground-700 text-xs rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 mb-8">
                <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  章节列表
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {chapters.length > 0 ? (
                    chapters.map((ch) => (
                      <a
                        key={ch.id}
                        href={`#chapter-${ch.id}`}
                        className="flex items-center justify-between px-4 py-2.5 bg-background-50 rounded-lg hover:bg-background-200 transition-colors duration-200 cursor-pointer border border-background-200"
                      >
                        <span className="text-sm text-foreground-950">{ch.title}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.updateTime}</span>
                      </a>
                    ))
                  ) : (
                    <p className="text-sm text-foreground-500 col-span-2 py-4 text-center">
                      章节信息加载中...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {related.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}